import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_sentryserver_f5c5a99c from 'nuxt_plugin_sentryserver_f5c5a99c' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_1f63b9aa from 'nuxt_plugin_sentryclient_1f63b9aa' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_plugin_f5294ed4 from 'nuxt_plugin_plugin_f5294ed4' // Source: ./vuetify/plugin.js (mode: 'all')
import nuxt_plugin_gtm_8badf878 from 'nuxt_plugin_gtm_8badf878' // Source: ./gtm.js (mode: 'all')
import nuxt_plugin_axios_350cb96e from 'nuxt_plugin_axios_350cb96e' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_deviceplugin_b179fa0e from 'nuxt_plugin_deviceplugin_b179fa0e' // Source: ./device.plugin.js (mode: 'all')
import nuxt_plugin_onetrust_6d2ea6ea from 'nuxt_plugin_onetrust_6d2ea6ea' // Source: ../plugins/onetrust.server (mode: 'server')
import nuxt_plugin_partner_f1a47bc6 from 'nuxt_plugin_partner_f1a47bc6' // Source: ../plugins/partner.server (mode: 'server')
import nuxt_plugin_axios_2228ef02 from 'nuxt_plugin_axios_2228ef02' // Source: ../plugins/axios (mode: 'all')
import nuxt_plugin_unleash_6a5d7ffe from 'nuxt_plugin_unleash_6a5d7ffe' // Source: ../plugins/unleash.server (mode: 'server')
import nuxt_plugin_unleash_a03bf6ee from 'nuxt_plugin_unleash_a03bf6ee' // Source: ../plugins/unleash.client (mode: 'client')
import nuxt_plugin_theme_de773a08 from 'nuxt_plugin_theme_de773a08' // Source: ../plugins/theme.server (mode: 'server')
import nuxt_plugin_partners_3358b572 from 'nuxt_plugin_partners_3358b572' // Source: ../plugins/partners.server (mode: 'server')
import nuxt_plugin_filters_2abc1387 from 'nuxt_plugin_filters_2abc1387' // Source: ../plugins/filters (mode: 'all')
import nuxt_plugin_vuetify_e5914fcc from 'nuxt_plugin_vuetify_e5914fcc' // Source: ../plugins/vuetify (mode: 'all')
import nuxt_plugin_clipboard_41299a22 from 'nuxt_plugin_clipboard_41299a22' // Source: ../plugins/clipboard (mode: 'all')
import nuxt_plugin_sanitize_624dab46 from 'nuxt_plugin_sanitize_624dab46' // Source: ../plugins/sanitize (mode: 'all')
import nuxt_plugin_sentry_40a2c6d3 from 'nuxt_plugin_sentry_40a2c6d3' // Source: ../plugins/sentry (mode: 'all')
import nuxt_plugin_storage_25489bf2 from 'nuxt_plugin_storage_25489bf2' // Source: ../plugins/storage (mode: 'all')
import nuxt_plugin_browserSupport_d68fc2c4 from 'nuxt_plugin_browserSupport_d68fc2c4' // Source: ../plugins/browserSupport.client (mode: 'client')
import nuxt_plugin_focusReset_41cf6a64 from 'nuxt_plugin_focusReset_41cf6a64' // Source: ../plugins/focusReset.client (mode: 'client')
import nuxt_plugin_onetrust_a30d1dda from 'nuxt_plugin_onetrust_a30d1dda' // Source: ../plugins/onetrust.client (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root ? this.$root.$options.$nuxt : null
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const store = createStore(ssrContext)
  const router = await createRouter(ssrContext, config, { store })

  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"Cambridge Press Open Research Platform server","htmlAttrs":{"lang":"en"},"meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"name":"description","hid":"description","content":"Cambridge Press Open Research Platform server"}],"link":[{"rel":"stylesheet","href":"\u002Fengage\u002Fassets\u002Fpublic\u002Fshared\u002Ffonts.css"},{"rel":"stylesheet","type":"text\u002Fcss","href":"https:\u002F\u002Ffonts.googleapis.com\u002Fcss?family=Roboto:100,300,400,500,700,900&display=swap"}],"style":[],"script":[{"hid":"gtm-script","innerHTML":"if(!window._gtm_init){window._gtm_init=1;(function(w,n,d,m,e,p){w[d]=(w[d]==1||n[d]=='yes'||n[d]==1||n[m]==1||(w[e]&&w[e][p]&&w[e][p]()))?1:0})(window,navigator,'doNotTrack','msDoNotTrack','external','msTrackingProtectionEnabled');(function(w,d,s,l,x,y){w[x]={};w._gtm_inject=function(i){if(w.doNotTrack||w[x][i])return;w[x][i]=1;w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.async=true;j.src='https:\u002F\u002Fwww.googletagmanager.com\u002Fgtm.js?id='+i;f.parentNode.insertBefore(j,f);}})(window,document,'script','dataLayer','_gtm_ids','_gtm_inject')}"}],"noscript":[{"hid":"gtm-noscript","pbody":true,"innerHTML":""}],"__dangerouslyDisableSanitizersByTagID":{"gtm-script":["innerHTML"],"gtm-noscript":["innerHTML"]}},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      errPageReady: false,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        nuxt.errPageReady = false
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    beforeSerializeFns: ssrContext ? ssrContext.beforeSerializeFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.server && typeof nuxt_plugin_sentryserver_f5c5a99c === 'function') {
    await nuxt_plugin_sentryserver_f5c5a99c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_1f63b9aa === 'function') {
    await nuxt_plugin_sentryclient_1f63b9aa(app.context, inject)
  }

  if (typeof nuxt_plugin_plugin_f5294ed4 === 'function') {
    await nuxt_plugin_plugin_f5294ed4(app.context, inject)
  }

  if (typeof nuxt_plugin_gtm_8badf878 === 'function') {
    await nuxt_plugin_gtm_8badf878(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_350cb96e === 'function') {
    await nuxt_plugin_axios_350cb96e(app.context, inject)
  }

  if (typeof nuxt_plugin_deviceplugin_b179fa0e === 'function') {
    await nuxt_plugin_deviceplugin_b179fa0e(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_onetrust_6d2ea6ea === 'function') {
    await nuxt_plugin_onetrust_6d2ea6ea(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_partner_f1a47bc6 === 'function') {
    await nuxt_plugin_partner_f1a47bc6(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_2228ef02 === 'function') {
    await nuxt_plugin_axios_2228ef02(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_unleash_6a5d7ffe === 'function') {
    await nuxt_plugin_unleash_6a5d7ffe(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_unleash_a03bf6ee === 'function') {
    await nuxt_plugin_unleash_a03bf6ee(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_theme_de773a08 === 'function') {
    await nuxt_plugin_theme_de773a08(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_partners_3358b572 === 'function') {
    await nuxt_plugin_partners_3358b572(app.context, inject)
  }

  if (typeof nuxt_plugin_filters_2abc1387 === 'function') {
    await nuxt_plugin_filters_2abc1387(app.context, inject)
  }

  if (typeof nuxt_plugin_vuetify_e5914fcc === 'function') {
    await nuxt_plugin_vuetify_e5914fcc(app.context, inject)
  }

  if (typeof nuxt_plugin_clipboard_41299a22 === 'function') {
    await nuxt_plugin_clipboard_41299a22(app.context, inject)
  }

  if (typeof nuxt_plugin_sanitize_624dab46 === 'function') {
    await nuxt_plugin_sanitize_624dab46(app.context, inject)
  }

  if (typeof nuxt_plugin_sentry_40a2c6d3 === 'function') {
    await nuxt_plugin_sentry_40a2c6d3(app.context, inject)
  }

  if (typeof nuxt_plugin_storage_25489bf2 === 'function') {
    await nuxt_plugin_storage_25489bf2(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_browserSupport_d68fc2c4 === 'function') {
    await nuxt_plugin_browserSupport_d68fc2c4(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_focusReset_41cf6a64 === 'function') {
    await nuxt_plugin_focusReset_41cf6a64(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_onetrust_a30d1dda === 'function') {
    await nuxt_plugin_onetrust_a30d1dda(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
